import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { LOGO_ONBG_URL } from "../lib/brand";
import { ArrowRight, Sparkles, Zap, HeartHandshake } from "lucide-react";

const KEY = "mpl_onboarded_v1";

function steps(isLender) {
  return [
    {
      icon: Sparkles,
      title: "Listen — we get it. Life's busy. 🍭",
      body:
        "You're not meant to live behind a computer. My Pocket Lender lets your thumbs do all the heavy lifting — " +
        "so you can get back to actually living. Here's the 20-second lowdown.",
    },
    isLender
      ? {
          icon: Zap,
          title: "Here's what it does",
          body:
            "You load a client's numbers once (or just upload a loan summary PDF and we auto-fill it). " +
            "Your realtors then send polished, on-brand pre-approvals themselves — you handle the loan estimates. You approve them " +
            "and set each client to Preapproved → Fully Approved. That's it.",
        }
      : {
          icon: Zap,
          title: "Here's what it does",
          body:
            "Your lender loads your clients with their buying power. You tap a client, punch in the offer price, " +
            "and fire off a gorgeous pre-approval in seconds — right from your phone. Email it or text it. Boom.",
        },
    isLender
      ? {
          icon: HeartHandshake,
          title: "Why it'll change your life",
          body:
            "No more 9pm \"can you send a pre-approval?\" texts. Fewer interruptions, faster closings, happier realtors — " +
            "and you look like the hero. 🦸 Next time someone calls, just say \"it's in the My Pocket Lender app.\"",
        }
      : {
          icon: HeartHandshake,
          title: "Why it'll change your life",
          body:
            "No more waiting on your lender or missing a beat. Win offers faster, wow your buyers, and send everything " +
            "yourself — right from your thumb. 👍 Welcome to the easy life.",
        },
  ];
}

export default function Onboarding() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);

  useEffect(() => {
    if (user && localStorage.getItem(KEY) !== "1") {
      const t = setTimeout(() => setOpen(true), 500);
      return () => clearTimeout(t);
    }
  }, [user]);

  if (!user) return null;
  const isLender = user.role === "admin";
  const s = steps(isLender);
  const step = s[i];
  const Icon = step.icon;
  const last = i === s.length - 1;

  const finish = () => {
    localStorage.setItem(KEY, "1");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? finish() : setOpen(v))}>
      <DialogContent
        className="bg-[#0d0d0d] border-white/10 text-white max-w-md overflow-hidden p-0"
        data-testid="onboarding-modal"
      >
        <DialogTitle className="sr-only">Welcome to My Pocket Lender</DialogTitle>
        <div className="relative px-7 pt-8 pb-7">
          <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(120%_80%_at_50%_-10%,rgba(255,59,48,0.22),transparent_60%)]" />
          <img src={LOGO_ONBG_URL} alt="" className="relative h-16 w-16 rounded-2xl mx-auto shadow-2xl shadow-[#ff3b30]/30" />

          <div className="relative mt-6 text-center min-h-[168px]">
            <div className="h-11 w-11 rounded-full bg-[#ff3b30]/15 text-[#ff3b30] flex items-center justify-center mx-auto mb-4">
              <Icon className="h-5 w-5" />
            </div>
            <h2 className="font-display text-2xl tracking-tight" data-testid="onboarding-title">{step.title}</h2>
            <p className="text-sm text-white/65 mt-3 leading-relaxed">{step.body}</p>
          </div>

          <div className="relative flex items-center justify-center gap-1.5 mt-6">
            {s.map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-[#ff3b30]" : "w-1.5 bg-white/20"}`}
              />
            ))}
          </div>

          <div className="relative mt-6 flex items-center gap-3">
            <button onClick={finish} data-testid="onboarding-skip" className="text-sm text-white/45 hover:text-white/70 transition-colors">
              Don't show this again
            </button>
            <Button
              onClick={() => (last ? finish() : setI(i + 1))}
              data-testid="onboarding-next"
              className="ml-auto h-11 px-6 bg-[#ff3b30] hover:bg-[#ff5147] text-white font-semibold rounded-xl"
            >
              {last ? "Let's go 🍭" : (<>Next <ArrowRight className="h-4 w-4 ml-1.5" /></>)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
